import {
  useDeleteBalanceAnchorMutation,
  useDeleteBalanceReadingMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type DeleteBalanceEntryModalUserProps = {
  /**
   * Anchors (user-recorded balances) and readings (Plaid-synced balances)
   * live in separate collections with separate delete mutations; the caller
   * knows which kind of row it is showing.
   */
  kind: 'anchor' | 'reading';
  entryId: string;
  entryDate: string;
  onDeleted?: () => void;
};

export const DeleteBalanceEntryModal = (
  props: DeleteBalanceEntryModalUserProps & SystemProps,
) => {
  const {kind, entryId, entryDate, onDeleted, onRequestClose} = props;
  const [deleteAnchor, {loading: deletingAnchor}] =
    useDeleteBalanceAnchorMutation();
  const [deleteReading, {loading: deletingReading}] =
    useDeleteBalanceReadingMutation();
  const loading = deletingAnchor || deletingReading;
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      if (kind === 'anchor') {
        await deleteAnchor({variables: {anchorId: entryId}});
      } else {
        await deleteReading({variables: {readingId: entryId}});
      }
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete balance');
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        onClick={() => void handleDelete()}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header={kind === 'anchor' ? 'Delete balance' : 'Delete synced balance'}
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to delete the balance for{' '}
        <strong>{entryDate}</strong>? This action cannot be undone.
      </p>
    </Modal>
  );
};
